import React from 'react'
import { View } from 'react-native'
import RNPickerSelect, { type Item, type PickerStyle } from 'react-native-picker-select'
import styled from 'styled-components/native'

import I18n from 'APP/I18n'
import { getColourScheme, ColourScheme } from 'APP/Helpers/AppearanceUtils'

import Typography from 'APP/Converse/Typography'
import Infotip from 'APP/Converse/Inputs/Infotip'
import Styles from 'APP/Converse/Inputs/style'
import { ShadowView } from 'APP/Converse/ShadowView'
import Icon from 'APP/Converse/Icon'

// Workaround until bottom margin can be removed from StyleInputContainer
// in APP/Converse/Inputs/styles
const StyledInputContainerWithOptionalMargin = styled.View<{ noBottomMargin?: boolean }>`
  gap: 4px;
  margin-bottom: ${({ theme, noBottomMargin }) =>
    noBottomMargin ? '0px' : `${theme.metrics.baseMargin}px`};
`

export interface PickerProps {
  value: string
  label?: string
  showRequiredAsterisk?: boolean
  infotip?: string
  isPersistentInfotip?: boolean
  isFocused?: boolean
  error?: string
  disabled?: boolean
  onValueChange: (value: string) => void
  analyticsName?: string
  onFocus?: () => void
  items: Item[]
  testID?: string
  placeholder?: string
  onDonePress?: () => void
  noBottomMargin?: boolean
}

const Picker = ({
  value,
  label,
  showRequiredAsterisk,
  infotip,
  isPersistentInfotip,
  isFocused,
  error,
  disabled,
  onValueChange,
  onFocus,
  items,
  testID,
  placeholder = '',
  onDonePress,
  noBottomMargin,
}: PickerProps) => {
  const isDarkTheme = getColourScheme() === ColourScheme.DARK

  return (
    <StyledInputContainerWithOptionalMargin testID={testID} noBottomMargin={noBottomMargin}>
      {label && (
        <View style={{ flexDirection: 'row' }}>
          <Typography variant="bodyNormalBold" testID="PICKER_LABEL">
            {label}
          </Typography>
          {showRequiredAsterisk && (
            <Typography variant="bodyNormalBold" color="error" testID="PICKER_LABEL_ASTERISK">
              &nbsp;*
            </Typography>
          )}
        </View>
      )}
      <ShadowView>
        <RNPickerSelect
          placeholder={{ label: placeholder }}
          doneText={String(I18n.t('Common.formNavBar.done'))}
          value={value}
          onValueChange={onValueChange}
          onDonePress={onDonePress}
          items={items}
          style={(disabled ? Styles.disabledPickerStyles : Styles.pickerStyles) as PickerStyle}
          pickerProps={{
            accessibilityLabel: placeholder,
          }}
          Icon={() => (
            <Icon
              src="chevron-down"
              variant="material"
              style={disabled ? Styles.disabledPickerStyles.icon : Styles.pickerStyles.icon}
              color={disabled ? 'darkText' : 'text'}
            />
          )}
          onOpen={onFocus}
          useNativeAndroidPickerStyle={false}
          darkTheme={isDarkTheme}
          disabled={disabled}
        />
      </ShadowView>
      <Infotip
        error={error}
        infotip={infotip}
        isFocused={isFocused}
        isPersistent={isPersistentInfotip}
      />
    </StyledInputContainerWithOptionalMargin>
  )
}

export default Picker
